// Shared multi-step wizard chrome, used by BOTH case-submission surfaces — the clinic portal and the
// lab's new-case form — so a doctor and a receptionist learn the same pattern.
import type { ReactNode } from 'react';

/** Clickable progress header. Only completed steps are navigable; you can't skip ahead. */
export function WizardStepper({ steps, step, onJump }: { steps: readonly string[]; step: number; onJump: (i: number) => void }) {
  return (
    <ol className="mb-4 flex items-center gap-1 overflow-x-auto pb-1">
      {steps.map((s, i) => {
        const done = i < step;
        const on = i === step;
        return (
          <li key={s} className="flex flex-1 items-center gap-1">
            <button
              type="button"
              onClick={() => i < step && onJump(i)}
              disabled={i > step}
              className={`flex min-w-max items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition ${
                on ? 'bg-primary text-primary-fg' : done ? 'bg-status-progress-bg text-status-progress-fg hover:opacity-80' : 'text-muted'
              } ${i > step ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <span className={`grid h-5 w-5 place-items-center rounded-full text-xs font-bold ${on ? 'bg-white/20' : done ? 'bg-status-progress-fg/15' : 'bg-surface-2'}`}>
                {done ? '✓' : i + 1}
              </span>
              <span className="hidden sm:inline">{s}</span>
            </button>
            {i < steps.length - 1 && <span className={`h-px flex-1 ${done ? 'bg-status-progress-fg/40' : 'bg-line'}`} />}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * Back / Continue / Submit bar. `fixed` pins it to the viewport for a full-page wizard; `inline`
 * sits at the end of a modal.
 */
export function WizardFooter({
  step,
  last,
  canContinue,
  busy,
  progress,
  submitLabel = 'Submit',
  variant = 'inline',
  onBack,
  onNext,
  onSubmit,
  onCancel,
}: {
  step: number;
  last: number;
  canContinue: boolean;
  busy: boolean;
  progress?: string | null;
  submitLabel?: ReactNode;
  variant?: 'inline' | 'fixed';
  onBack: () => void;
  onNext: () => void;
  onSubmit: () => void;
  onCancel?: () => void;
}) {
  const bar = (
    <div className={variant === 'fixed' ? 'mx-auto flex max-w-3xl items-center gap-3' : 'flex items-center gap-2 border-t border-line pt-3'}>
      {step > 0 ? (
        <button type="button" onClick={onBack} disabled={busy} className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink transition hover:bg-surface-2 disabled:opacity-40">
          Back
        </button>
      ) : onCancel ? (
        <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-ink transition hover:bg-surface-2 disabled:opacity-40">
          Cancel
        </button>
      ) : null}
      {progress && <span className="text-xs text-muted">{progress}</span>}
      {step < last ? (
        <button
          type="button"
          onClick={onNext}
          disabled={!canContinue || busy}
          className="ml-auto rounded-lg bg-primary px-6 py-2 text-sm font-semibold text-primary-fg transition hover:opacity-90 disabled:opacity-40"
        >
          Continue
        </button>
      ) : (
        <button
          type="button"
          onClick={onSubmit}
          disabled={busy}
          className="ml-auto rounded-lg bg-accent px-6 py-2 text-sm font-semibold text-accent-fg transition hover:opacity-90 disabled:opacity-50"
        >
          {submitLabel}
        </button>
      )}
    </div>
  );
  if (variant === 'fixed') {
    return <div className="fixed inset-x-0 bottom-[calc(3.75rem+env(safe-area-inset-bottom))] z-bar border-t border-line bg-surface px-4 py-3 shadow-bar lg:bottom-0">{bar}</div>;
  }
  return bar;
}
